import type { FileRecord } from "@cohesive/api-client";
import { getEntityName } from "@cohesive/ui-components";
import type { Node } from "@xyflow/react";

export type ContentCategory = "file" | "entity" | "object";
/** Ordering within a folder — layer order (build order: canvas z) or name. */
export type ContentsSort = "layer" | "name";
/** The Show menu's folder vocabulary — hiding a kind hides its whole
 *  top-level folder (DS 021: "Show hides whole folders"). */
export type ContentsKind =
  | "sections"
  | "files"
  | "entities"
  | "shapes"
  | "notes"
  | "labels"
  | "widgets";

export const CONTENTS_KIND_LABELS: Record<ContentsKind, string> = {
  sections: "Sections",
  files: "Files",
  entities: "Entities",
  shapes: "Shapes",
  notes: "Notes",
  labels: "Labels",
  widgets: "Widgets",
};

/** Which Show kind governs a type folder. Platform groups (and the Other
 *  bucket) all ride the one Entities toggle. */
export function kindForGroupKey(key: string): ContentsKind {
  switch (key) {
    case "__files__":
      return "files";
    case "__shapes__":
      return "shapes";
    case "__notes__":
      return "notes";
    case "__labels__":
      return "labels";
    case "__widgets__":
      return "widgets";
    default:
      return "entities";
  }
}

/** Unified canvas-content item for the rail contents list. */
export interface ContentItem {
  category: ContentCategory;
  id: string;
  fileRecord?: FileRecord;
  node?: Node;
  parentSectionId?: string;
  placed: boolean;
  /** The node exists but carries `hidden: true` — hidden from the canvas
   *  surface, still listed in the rail with a "Show on canvas" affordance. */
  hidden: boolean;
}

export interface ContentGroup {
  key: string;
  label: string;
  platform: string | null;
  items: ContentItem[];
}

export interface SectionViewEntry {
  section: Node;
  items: ContentItem[];
}

export interface SectionViewModel {
  entries: SectionViewEntry[];
  rootItems: ContentItem[];
  totalVisible: number;
}

/** Node types that are pure visualization or chrome and should never appear in the list. */
export const EXCLUDED_NODE_TYPES = new Set<string>([
  "chatSession",
  "canvasConnector",
]);

const PLATFORM_DISPLAY_NAMES: Record<string, string> = {
  github: "GitHub",
  gitlab: "GitLab",
  gmail: "Gmail",
  google_calendar: "Google Calendar",
  google_docs: "Google Docs",
  google_drive: "Google Drive",
  hubspot: "HubSpot",
  salesforce: "Salesforce",
  slack: "Slack",
  notion: "Notion",
  linear: "Linear",
  jira: "Jira",
  asana: "Asana",
  trello: "Trello",
  monday: "Monday",
  airtable: "Airtable",
  confluence: "Confluence",
  zendesk: "Zendesk",
  intercom: "Intercom",
  zoom: "Zoom",
};

export function getEntityPlatform(
  data: Record<string, unknown> | undefined,
): string | null {
  const entity = data?.entity as { platform?: unknown } | undefined;
  return typeof entity?.platform === "string" && entity.platform
    ? entity.platform
    : null;
}

export function categorizeNode(node: Node): ContentCategory {
  const platform = getEntityPlatform(
    node.data as Record<string, unknown> | undefined,
  );
  if (platform) {
    const entity = (node.data as { entity?: { type?: string } } | undefined)
      ?.entity;
    if (entity?.type === "file_record") return "file";
    return "entity";
  }
  return "object";
}

export function itemMatchesSearch(item: ContentItem, q: string): boolean {
  if (item.fileRecord) {
    return item.fileRecord.path.toLowerCase().includes(q);
  }
  if (item.node) return nodeMatchesSearch(item.node, q);
  return false;
}

export function getNodeTitle(node: Node): string {
  const d = node.data as Record<string, unknown> | undefined;
  const entityTitle = d?.entity ? getEntityName(d.entity as never) : "";
  if (entityTitle) return entityTitle;
  if (typeof d?.title === "string" && d.title) return d.title;
  if (typeof d?.content === "string" && d.content.trim()) {
    return d.content.slice(0, 40).trim();
  }
  if (node.type === "canvasShape") {
    return formatShapeLabel(
      typeof d?.shape_type === "string" ? d.shape_type : "shape",
    );
  }
  if (node.type === "textNote") return "Text note";
  if (node.type === "canvasText") return "Text label";
  if (node.type === "section") return "Section";
  if (node.type === "widgetNode") return "Widget";
  return "Untitled";
}

export function nodeMatchesSearch(node: Node, q: string): boolean {
  if (getNodeTitle(node).toLowerCase().includes(q)) return true;
  const d = node.data as Record<string, unknown> | undefined;
  const platform = getEntityPlatform(d);
  if (!platform) return false;
  if (platform.toLowerCase().includes(q)) return true;
  return (PLATFORM_DISPLAY_NAMES[platform] ?? platform)
    .toLowerCase()
    .includes(q);
}

export function formatShapeLabel(shapeType: string): string {
  return shapeType
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function capitalize(value: string): string {
  return value.replace(/\b\w/g, (c) => c.toUpperCase());
}

export function leafFileName(path: string): string {
  const cleaned = path.endsWith("/") ? path.slice(0, -1) : path;
  const segs = cleaned.split("/").filter(Boolean);
  return segs[segs.length - 1] || path;
}

export function buildAllContentItems(params: {
  fileRecords: FileRecord[];
  rawNodes: Node[];
  fileRecordIdToNodeId: Map<string, string>;
  fileRecordNodeIds: Set<string>;
  nodesById: Map<string, Node>;
}): ContentItem[] {
  const {
    fileRecords,
    rawNodes,
    fileRecordIdToNodeId,
    fileRecordNodeIds,
    nodesById,
  } = params;
  const items: ContentItem[] = [];

  for (const r of fileRecords) {
    const nodeId = fileRecordIdToNodeId.get(r.id);
    const node = nodeId ? nodesById.get(nodeId) : undefined;
    items.push({
      category: "file",
      id: r.id,
      fileRecord: r,
      node,
      parentSectionId: node?.parentId,
      placed: !!node,
      hidden: node?.hidden === true,
    });
  }

  for (const n of rawNodes) {
    if (EXCLUDED_NODE_TYPES.has(n.type ?? "")) continue;
    if (fileRecordNodeIds.has(n.id)) continue;
    items.push({
      category: categorizeNode(n),
      id: n.id,
      node: n,
      parentSectionId: n.parentId,
      placed: true,
      hidden: n.hidden === true,
    });
  }

  return items;
}

export function filterVisibleItems(
  allItems: ContentItem[],
  activeFilter: Set<ContentCategory>,
  searchQuery: string,
): ContentItem[] {
  const q = searchQuery.toLowerCase();
  return allItems.filter((item) => {
    if (!activeFilter.has(item.category)) return false;
    if (!q) return true;
    return itemMatchesSearch(item, q);
  });
}

export function groupItemsByType(items: ContentItem[]): ContentGroup[] {
  const groups = new Map<string, ContentGroup>();

  const ensure = (
    key: string,
    label: string,
    platform: string | null,
  ): ContentGroup => {
    let g = groups.get(key);
    if (!g) {
      g = { key, label, platform, items: [] };
      groups.set(key, g);
    }
    return g;
  };

  for (const item of items) {
    if (item.category === "file") {
      ensure("__files__", "Files", null).items.push(item);
      continue;
    }
    if (item.category === "entity") {
      const node = item.node;
      const platform = node
        ? getEntityPlatform(node.data as Record<string, unknown> | undefined)
        : null;
      if (platform === "cohesive") {
        ensure("cohesive", "Cohesive", "cohesive").items.push(item);
      } else if (platform) {
        const label = PLATFORM_DISPLAY_NAMES[platform] ?? capitalize(platform);
        ensure(platform, label, platform).items.push(item);
      } else {
        ensure("__other__", "Other", null).items.push(item);
      }
      continue;
    }
    const t = item.node?.type ?? "";
    switch (t) {
      case "canvasShape":
        ensure("__shapes__", "Shapes", null).items.push(item);
        break;
      case "textNote":
        ensure("__notes__", "Text Notes", null).items.push(item);
        break;
      case "canvasText":
        ensure("__labels__", "Text Labels", null).items.push(item);
        break;
      case "section":
        ensure("__sections__", "Sections", null).items.push(item);
        break;
      case "widgetNode":
        ensure("__widgets__", "Widgets", null).items.push(item);
        break;
      default:
        ensure("__other__", "Other", null).items.push(item);
    }
  }

  return Array.from(groups.values()).sort(compareGroups);
}

function compareGroups(a: ContentGroup, b: ContentGroup): number {
  const aFiles = a.key === "__files__";
  const bFiles = b.key === "__files__";
  if (aFiles !== bFiles) return aFiles ? -1 : 1;
  const aSynthetic = a.key.startsWith("__");
  const bSynthetic = b.key.startsWith("__");
  if (aSynthetic !== bSynthetic) return aSynthetic ? 1 : -1;
  return a.label.localeCompare(b.label);
}

export function buildSectionView(params: {
  rawNodes: Node[];
  visibleItems: ContentItem[];
  searchQuery: string;
}): SectionViewModel {
  const { rawNodes, visibleItems, searchQuery } = params;
  const sectionNodes = rawNodes.filter((n) => n.type === "section");
  const sectionIds = new Set(sectionNodes.map((s) => s.id));

  const itemsBySection = new Map<string, ContentItem[]>();
  const rootItems: ContentItem[] = [];
  for (const item of visibleItems) {
    if (item.node?.type === "section") continue;
    if (item.parentSectionId && sectionIds.has(item.parentSectionId)) {
      const arr = itemsBySection.get(item.parentSectionId) ?? [];
      arr.push(item);
      itemsBySection.set(item.parentSectionId, arr);
    } else {
      rootItems.push(item);
    }
  }

  const q = searchQuery.toLowerCase();
  const entries: SectionViewEntry[] = sectionNodes
    .map((section) => {
      const items = itemsBySection.get(section.id) ?? [];
      if (q) {
        const titleMatches = getNodeTitle(section).toLowerCase().includes(q);
        if (items.length === 0 && !titleMatches) return null;
      }
      return { section, items };
    })
    .filter((e): e is SectionViewEntry => e !== null);

  const totalVisible =
    entries.reduce((sum, e) => sum + e.items.length, 0) + rootItems.length;

  return { entries, rootItems, totalVisible };
}

/** Display name for within-folder name sort. */
export function contentItemName(item: ContentItem): string {
  if (item.fileRecord) return leafFileName(item.fileRecord.path);
  if (item.node) return getNodeTitle(item.node);
  return "";
}

/** Orders a folder's items: layer keeps build order (canvas z), name sorts. */
export function sortContentItems(
  items: ContentItem[],
  sort: ContentsSort,
): ContentItem[] {
  if (sort === "layer") return items;
  return [...items].sort((a, b) =>
    contentItemName(a).localeCompare(contentItemName(b), undefined, {
      sensitivity: "base",
      numeric: true,
    }),
  );
}

/** Visual order of the one-lens body: section items first, then the
 *  unsectioned items in their type folders — range-select follows it. */
export function buildVisibleItemIdOrder(params: {
  sectionView: SectionViewModel;
  rootTypeGroups: ContentGroup[];
}): string[] {
  const { sectionView, rootTypeGroups } = params;
  const ids: string[] = [];
  for (const e of sectionView.entries) {
    for (const it of e.items) ids.push(it.id);
  }
  for (const g of rootTypeGroups) {
    for (const it of g.items) ids.push(it.id);
  }
  return ids;
}

export function buildFileRecordLookups(
  fileRecords: FileRecord[],
  rawNodes: Node[],
) {
  const fileRecordIds = new Set(fileRecords.map((r) => r.id));
  const nodesById = new Map<string, Node>();
  for (const n of rawNodes) nodesById.set(n.id, n);

  // A node's entity id matches its row directly OR through a link row's
  // TARGET id — a promoted file's node keeps the Library target id while
  // the canvas row is the reference (same rule as
  // pages/canvas/utils/resolveFileRecordFromList). Without the fallback the
  // link renders as a phantom "not placed" row while its node shows as an
  // unmatched second row — the exact split-state D2 forbids.
  const rowByEntityId = new Map<string, FileRecord>();
  for (const r of fileRecords) rowByEntityId.set(r.id, r);
  for (const r of fileRecords) {
    if (
      r.target_file_record_id &&
      !rowByEntityId.has(r.target_file_record_id)
    ) {
      rowByEntityId.set(r.target_file_record_id, r);
    }
  }

  const fileRecordIdToNodeId = new Map<string, string>();
  for (const n of rawNodes) {
    const entityId = (
      n.data as { entity?: { data?: { id?: string } } } | undefined
    )?.entity?.data?.id;
    if (!entityId) continue;
    const row = rowByEntityId.get(entityId);
    if (row && !fileRecordIdToNodeId.has(row.id)) {
      fileRecordIdToNodeId.set(row.id, n.id);
    }
  }

  const fileRecordNodeIds = new Set(fileRecordIdToNodeId.values());

  return { fileRecordIds, nodesById, fileRecordIdToNodeId, fileRecordNodeIds };
}
